<template>
  <a :href="link" :aria-label="profile.profile_name">
    <div class="group flex flex-col gap-3">
      <div class="relative block">
        <img
          :src="cardImage"
          :alt="profile.profile_name"
          class="aspect-video w-full rounded-xl object-cover"
          @load="handleImageLoad"
        />

        <div v-if="isImageLoading" class="absolute inset-0 flex items-center justify-center rounded-xl bg-gray-100">
          <IAISpinner :size="3" />
        </div>

        <div
          class="absolute inset-0 cursor-pointer rounded-xl transition-colors duration-300 group-hover:bg-black/20"
        ></div>

        <div
          v-if="showFormatTag"
          class="absolute left-4 top-4 flex cursor-pointer items-center justify-center transition-all duration-300 md:opacity-0 md:group-hover:opacity-100"
        >
          <IAITag size="small" variant="default">{{ profile.format }}</IAITag>
        </div>

        <IAITag
          v-if="profile.discount"
          class="absolute right-0 top-4 flex cursor-pointer items-center justify-center"
          size="small"
          variant="pink"
          rounded-side="left"
        >
          -{{ profile.discount.discount_percentage }}%
        </IAITag>
      </div>

      <div class="flex flex-row gap-2">
        <div class="h-10 w-10 flex-shrink-0 self-center">
          <img :src="profile.seller_image" :alt="profile.seller_name" class="h-full w-full rounded-full object-cover" />
        </div>

        <div class="flex w-full min-w-0 flex-col justify-between gap-[0.125rem]">
          <div class="flex flex-row justify-between gap-2">
            <ProfileNameAndTags :profile-name="profile.profile_name" :tags="profile.tags" />

            <ProfileCardPrice :original-price="profile.price" :discount="profile.discount" />
          </div>

          <span class="text-sm">{{ profile.seller_name }}</span>
        </div>
      </div>
    </div>
  </a>
</template>

<script lang="ts" setup>
import { IAITag, IAISpinner } from "@/core-ui-vue";
import ProfileNameAndTags from "@/features/profile-shop/components/profileCard/ProfileNameAndTags.vue";
import ProfileCardPrice from "@/features/profile-shop/components/profileCard/ProfileCardPrice.vue";
import { ProfileGridObject } from "@/features/profile-shop/services/api";
import { universalStorage } from "@/utils/universal-storage";
import { computed, ref, watch } from "vue";
import { ProfileLinkSource } from "@/features/profile-shop/utils";

interface Props {
  profile: ProfileGridObject;
  source: ProfileLinkSource;
  selectedCompareStyleImage: string;
}

const props = withDefaults(defineProps<Props>(), {
  selectedCompareStyleImage: "original",
});

const isImageLoading = ref(false);

const { mobile_or_desktop } = universalStorage.getAll();

const showFormatTag = computed(() => {
  return mobile_or_desktop === "desktop" || props.profile.format === "JPG";
});

const cardImage = computed(() => {
  const previewImage = props.profile.edited_preview_images[props.selectedCompareStyleImage];

  if (props.selectedCompareStyleImage === "original" || !previewImage) {
    return props.profile.cover_image;
  }

  return previewImage;
});

const link = computed(() => {
  const { url: baseUrl } = universalStorage.getAll();
  const url = new URL(`/profile-shop/${props.profile.slug}`, baseUrl);
  url.searchParams.set("source", props.source);
  return url.toString();
});

const handleImageLoad = () => {
  isImageLoading.value = false;
};

watch(
  () => props.selectedCompareStyleImage,
  () => {
    isImageLoading.value = true;
  }
);
</script>
